import { useEffect, useState } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Sparkline } from "@/components/Sparkline";
import { api } from "@/lib/api";
import { fmt, dirColor } from "@/lib/format";
import { RecordOutcomeDialog } from "@/components/RecordOutcomeDialog";

export function IndicatorDetailDrawer({ code, open, onClose, onRecorded }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !code) return;
    setDetail(null);
    api.get(`/indicators/${code}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [open, code]);

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent
        data-testid="prediction-detail-drawer"
        className="bg-[#0A0D14] border-white/10 text-[#F9FAFB] max-h-[90vh]"
      >
        {detail && (
          <div className="mx-auto w-full max-w-2xl overflow-y-auto px-5 pb-8">
            <DrawerHeader className="px-0">
              <DrawerTitle className="font-display text-xl flex items-center justify-between">
                <span>{detail.name}</span>
                <span className="text-[11px] font-mono-num text-[#6B7280]">{detail.category_label}</span>
              </DrawerTitle>
              <DrawerDescription className="text-xs text-[#6B7280] text-left">
                Full prediction reasoning — history, Model A forecast and leading indicators.
              </DrawerDescription>
            </DrawerHeader>

            {detail.explain && (
              <div data-testid="detail-explain" className="glass rounded-2xl p-4 mb-5 text-sm leading-relaxed">
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-1.5">What is this &amp; why gold cares</div>
                <p className="text-[#D1D5DB]">{detail.explain.what}</p>
                <p className="text-[#9CA3AF] mt-1.5 text-xs">{detail.explain.why_gold}</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mb-5">
              <Stat label="Previous" value={fmt(detail.previous, detail.unit)} />
              <Stat label="Latest actual" value={fmt(detail.latest, detail.unit)} accent />
              <Stat label="Consensus (info)" value={fmt(detail.consensus, detail.unit)} />
            </div>

            <div className="glass rounded-2xl p-4 mb-5">
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-2">14-reading history</div>
              <Sparkline data={detail.history.map((h) => h.value)} color="#F59E0B" width={560} height={70} />
            </div>

            {detail.prediction && (
              <div className="glass rounded-2xl p-4 mb-5">
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-3">Model A — next release forecast</div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-display text-2xl font-bold ${dirColor(detail.prediction.predicted_direction)}`}>
                    {detail.prediction.predicted_direction} {fmt(detail.prediction.predicted_value, detail.unit)}
                  </span>
                  <span className="font-mono-num text-[#F59E0B] text-lg">{Math.round(detail.prediction.probability * 100)}%</span>
                </div>
                <div className="text-xs text-[#9CA3AF] font-mono-num">
                  expected range {fmt(detail.prediction.range_low, detail.unit)} – {fmt(detail.prediction.range_high, detail.unit)}
                </div>
                <div className="text-xs text-[#9CA3AF] font-mono-num mt-1">
                  gold impact: <span className={detail.prediction.hawkish_surprise >= 0 ? "text-[#EF4444]" : "text-[#10B981]"}>{detail.prediction.hawkish_surprise >= 0 ? "bad for gold" : "good for gold"} ({detail.prediction.hawkish_surprise > 0 ? "+" : ""}{detail.prediction.hawkish_surprise})</span>
                </div>
              </div>
            )}

            {detail.feeders?.length > 0 && (
              <div className="glass rounded-2xl p-4 mb-5">
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-3">Leading indicators feeding this forecast</div>
                <div className="flex flex-col gap-2">
                  {detail.feeders.map((f) => (
                    <div key={f.code} data-testid={`feeder-${f.code}`} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-[#9CA3AF] min-w-0 truncate">{f.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {f.effect && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border font-mono-num ${f.effect === "lifts" ? "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10" : "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10"}`}>
                            {f.effect === "lifts" ? "pushes up" : "pushes down"}
                          </span>
                        )}
                        <span className={`font-mono-num ${dirColor(f.direction)}`}>{f.direction} · {fmt(f.latest, f.unit)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.prediction?.status === "pending" && (
              <RecordOutcomeDialog
                predictionId={detail.prediction.prediction_id}
                indicatorName={detail.name}
                unit={detail.unit}
                onRecorded={() => { onRecorded?.(); onClose(); }}
              />
            )}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="glass rounded-xl p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
      <div className={`font-mono-num text-lg font-bold mt-1 ${accent ? "text-[#F59E0B]" : ""}`}>{value}</div>
    </div>
  );
}
